import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiChevronDown,
  FiChevronRight,
  FiUser,
  FiUsers,
  FiSearch,
} from "react-icons/fi";
import { getOrgTree } from "../api/client";

const beltDot = (color) => {
  const map = { red: "#ef4444", yellow: "#eab308", green: "#10b981" };
  return (
    <span
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: map[color] || "#6b7280",
        marginRight: 6,
        flexShrink: 0,
      }}
    />
  );
};

const TreeNode = ({ node, depth = 0, onEmployeeClick, searchQuery }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const emp = node.employee;
  const hasChildren = node.direct_reports && node.direct_reports.length > 0;
  const score = emp.latest_score_percentage;
  const beltColor = emp.belt_color;

  // If searching, auto-expand nodes that have matching descendants
  const matchesSearch =
    searchQuery &&
    (emp.name?.toLowerCase().includes(searchQuery) ||
      emp.designation?.toLowerCase().includes(searchQuery) ||
      emp.email?.toLowerCase().includes(searchQuery));

  const hasMatchingDescendant = searchQuery && hasChildren && _hasMatch(node, searchQuery);

  // Auto-expand when searching
  useEffect(() => {
    if (searchQuery && hasMatchingDescendant) {
      setExpanded(true);
    }
  }, [searchQuery, hasMatchingDescendant]);

  if (searchQuery && !matchesSearch && !hasMatchingDescendant) {
    return null;
  }

  const indent = depth * 24;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "8px 12px",
          paddingLeft: 12 + indent,
          background: matchesSearch
            ? "rgba(99, 102, 241, 0.08)"
            : depth % 2 === 0
            ? "transparent"
            : "rgba(255,255,255,0.015)",
          borderBottom: "1px solid var(--border-subtle)",
          cursor: hasChildren ? "pointer" : "default",
          transition: "background 0.15s",
        }}
        onClick={() => hasChildren && setExpanded(!expanded)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-card-hover)")}
        onMouseLeave={(e) =>
          (e.currentTarget.style.background = matchesSearch
            ? "rgba(99, 102, 241, 0.08)"
            : "transparent")
        }
      >
        {/* Expand/collapse toggle */}
        <span style={{ width: 20, flexShrink: 0, color: "var(--text-muted)" }}>
          {hasChildren ? (
            expanded ? (
              <FiChevronDown size={14} />
            ) : (
              <FiChevronRight size={14} />
            )
          ) : (
            <FiUser size={12} style={{ opacity: 0.4 }} />
          )}
        </span>

        {/* Name + Designation */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              fontWeight: depth < 2 ? 600 : 400,
              color: "var(--text-primary)",
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onEmployeeClick && onEmployeeClick(emp);
            }}
          >
            {emp.name}
          </span>
          <span
            style={{
              marginLeft: 8,
              color: "var(--text-muted)",
              fontSize: "0.75rem",
            }}
          >
            {emp.designation}
          </span>
        </div>

        {/* Belt dot + score */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
            minWidth: 80,
            justifyContent: "flex-end",
          }}
        >
          {beltDot(beltColor)}
          <span
            style={{
              fontSize: "0.78rem",
              fontWeight: 600,
              color: score != null ? "var(--text-primary)" : "var(--text-muted)",
              minWidth: 35,
              textAlign: "right",
            }}
          >
            {score != null ? `${score}%` : "—"}
          </span>
        </div>

        {/* Team size */}
        {hasChildren && (
          <span
            style={{
              marginLeft: 12,
              fontSize: "0.7rem",
              color: "var(--text-muted)",
              background: "var(--bg-card)",
              padding: "2px 8px",
              borderRadius: 10,
              whiteSpace: "nowrap",
            }}
          >
            <FiUsers size={10} style={{ marginRight: 3, verticalAlign: "middle" }} />
            {node.direct_reports.length}
          </span>
        )}
      </div>

      {/* Children */}
      <AnimatePresence>
        {expanded && hasChildren && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: "hidden" }}
          >
            {/* Vertical connector line */}
            <div style={{ position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  left: 22 + indent,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  background: "var(--border-subtle)",
                }}
              />
              {node.direct_reports.map((child, idx) => (
                <TreeNode
                  key={child.employee.id || idx}
                  node={child}
                  depth={depth + 1}
                  onEmployeeClick={onEmployeeClick}
                  searchQuery={searchQuery}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function _hasMatch(node, query) {
  const emp = node.employee;
  if (
    emp.name?.toLowerCase().includes(query) ||
    emp.designation?.toLowerCase().includes(query) ||
    emp.email?.toLowerCase().includes(query)
  ) {
    return true;
  }
  if (node.direct_reports) {
    return node.direct_reports.some((child) => _hasMatch(child, query));
  }
  return false;
}

function _countAll(node) {
  let count = 0;
  if (node.direct_reports) {
    count += node.direct_reports.length;
    for (const child of node.direct_reports) {
      count += _countAll(child);
    }
  }
  return count;
}

export default function OrgTree({ onEmployeeClick }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchTree = async () => {
      try {
        const res = await getOrgTree(null, 10);
        setTree(res.data || []);
      } catch (err) {
        console.error("Failed to load org tree:", err);
        setError("Failed to load organization tree");
      } finally {
        setLoading(false);
      }
    };
    fetchTree();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <div className="spinner" />
        <p style={{ color: "var(--text-secondary)", marginTop: 12 }}>Loading organization tree...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "var(--text-secondary)" }}>
        {error}
      </div>
    );
  }

  if (!tree || tree.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "var(--text-secondary)" }}>
        No organizational data available.
      </div>
    );
  }

  const totalPeople = tree.reduce((sum, node) => sum + 1 + _countAll(node), 0);
  const query = search.toLowerCase().trim();

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: "1.25rem",
              color: "var(--text-primary)",
            }}
          >
            Organization Tree
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "var(--text-muted)" }}>
            {totalPeople} people in your organization
          </p>
        </div>

        {/* Search */}
        <div style={{ position: "relative" }}>
          <FiSearch
            size={14}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-muted)",
            }}
          />
          <input
            type="text"
            placeholder="Search people..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "8px 12px 8px 32px",
              borderRadius: 8,
              border: "1px solid var(--border-subtle)",
              background: "var(--bg-input)",
              color: "var(--text-primary)",
              fontSize: "0.82rem",
              width: 220,
              outline: "none",
            }}
          />
        </div>
      </div>

      {/* Tree */}
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg, 12px)",
          overflow: "hidden",
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {tree.map((rootNode, idx) => (
          <TreeNode
            key={rootNode.employee.id || idx}
            node={rootNode}
            depth={0}
            onEmployeeClick={onEmployeeClick}
            searchQuery={query}
          />
        ))}
      </div>
    </div>
  );
}
